import { POSITIONING_VARIANT_STORAGE_KEY } from '@/constants/funnel';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** Derive the funnel step number from a pathname, or null if it's not a funnel page.
 *  Matches `/join`, `/join/profile`, `/join/location` and the variant branches
 *  (`/join/test-before-test`, `/join/test-before-test/profile`, …, `/join/checkup/...`). */
function deriveFunnelStep(pathname: string): 1 | 2 | 3 | null {
  if (!pathname.startsWith('/join')) return null;
  if (pathname.endsWith('/location')) return 3;
  if (pathname.endsWith('/profile')) return 2;
  return 1;
}

// This hook enables page view reports to the analytics packages
export default function useAnalyticsPageViews() {
  const analytics = useAnalyticsCalls();
  const location = useLocation();
  const previousPathnameRef = useRef('');

  useEffect(() => {
    // Skip generic page view for quizlet routes — they fire their own specific events
    if (location.pathname.startsWith('/quizlet/')) {
      previousPathnameRef.current = location.pathname;
      return;
    }

    if (!previousPathnameRef.current || previousPathnameRef.current !== location.pathname) {
      // Construct the previous page URL for SPA referrer tracking
      const previousPageUrl = previousPathnameRef.current
        ? `${window.location.origin}${previousPathnameRef.current}`
        : undefined; // On first page load, use document.referrer (external referrer)

      // Extract source from URL parameters for flows that use URL-based source tracking
      // (e.g., risk_assessment, risk_score_reveal)
      const searchParams = new URLSearchParams(location.search);
      const urlSource = searchParams.get('source');

      // Join-funnel enrichment: attach positioning_variant (set by FunnelGuard
      // on mount, persisted in sessionStorage so it survives Stripe 3DS
      // redirects) and funnel_step (derived from the path). Both are null on
      // non-funnel routes. Including them on the SDK-driven page view lets
      // dashboards filter and funnel-analyze without a parallel custom event.
      const funnelStep = deriveFunnelStep(location.pathname);
      const positioningVariant =
        funnelStep !== null && typeof window !== 'undefined'
          ? sessionStorage.getItem(POSITIONING_VARIANT_STORAGE_KEY)
          : null;

      analytics.callPageView({
        page_name: location.pathname,
        ...(previousPageUrl && { referrer: previousPageUrl }),
        ...(urlSource && { source: urlSource }),
        ...(funnelStep !== null && { funnel_step: funnelStep }),
        ...(positioningVariant && { positioning_variant: positioningVariant }),
      });

      // Update the previous pathname for next navigation
      previousPathnameRef.current = location.pathname;
    }
  }, [location.pathname, location.search, analytics]);
}
